/* eslint-disable no-console */
import {
  createIdpTokenBundle$,
  hasFreshSubjectToken,
  idpTokenBundle$,
  isFreshTokenBundle,
} from '@lseg-ciam/auth/token';
import { decodeJwtPayload, flog } from '@lseg-ciam/auth/utils';
import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { firstValueFrom, Subscription, take } from 'rxjs';
import { rigSharedStyles } from './heimdall-rig-styles.js';

@customElement('heimdall-rig-idp-bundle')
export class HeimdallRigIdpBundle extends LitElement {
  @state() _subscription: Subscription | null = null;
  @state() _isSubscribed = false;
  @state() _lane: string | undefined;

  @state() _headline = '×';
  @state() _bundleJson = '';
  @state() _promiseResult = '';

  onLaneChanged(event: Event) {
    this._lane = (event.target as HTMLInputElement).value || undefined;
    this.clearSubscription();
  }

  onSetLane(lane?: string) {
    this._lane = lane;
    this.clearSubscription();
  }

  onSubscriptionRequestChanged(event: Event) {
    const { checked } = event.target as HTMLInputElement;

    if (!checked) {
      this.clearSubscription();
      return;
    }

    this._isSubscribed = true;
    this._subscription = new Subscription();

    const bundle$ = this._lane
      ? createIdpTokenBundle$(this._lane)
      : idpTokenBundle$;

    const laneLabel = this._lane ? ` [${this._lane}]` : '';

    this._subscription.add(
      bundle$
        .pipe(
          flog(
            `IDP bundle on RIG${laneLabel}`,
            'background: #ffe4b3; color: #663300; padding: 5px;'
          )
        )
        .subscribe((bundle) => this.renderBundle(bundle))
    );
  }

  async onGetBundle() {
    this._promiseResult = 'Loading...';
    try {
      const bundle$ = this._lane
        ? createIdpTokenBundle$(this._lane)
        : idpTokenBundle$;
      const bundle = await firstValueFrom(bundle$.pipe(take(1)));
      this._promiseResult = bundle
        ? `✓ idp=${bundle.idpConfig?.idpKey ?? '?'}`
        : 'null';
      console.log(
        `%cIDP Bundle (one-shot)${this._lane ? ` [${this._lane}]` : ''}`,
        'background: #b3ffd9; color: #006633; padding: 5px;',
        bundle
      );
    } catch (e) {
      this._promiseResult = `Error: ${String(e)}`;
      console.error('get idp bundle failed:', e);
    }
  }

  private renderBundle(bundle: unknown) {
    if (!bundle) {
      this._headline = 'null';
      this._bundleJson = '';
      return;
    }
    const b = bundle as {
      tokenSet: { access_token?: string; id_token?: string };
      idpConfig: { idpKey?: string; clientId?: string; clientType?: string };
      refreshInfo: unknown;
      ref?: string;
    };
    const fresh =
      isFreshTokenBundle(b as never) && hasFreshSubjectToken(b as never);
    this._headline = `idp=${b.idpConfig?.idpKey ?? '?'} · client=${
      b.idpConfig?.clientId ?? '?'
    } · ${fresh ? 'FRESH' : 'STALE'}`;
    const decoded = {
      tokenSet: {
        access_token: b.tokenSet.access_token
          ? decodeJwtPayload(b.tokenSet.access_token)
          : null,
        id_token: b.tokenSet.id_token
          ? decodeJwtPayload(b.tokenSet.id_token)
          : null,
      },
      idpConfig: b.idpConfig,
      refreshInfo: b.refreshInfo,
      ref: b.ref,
    };
    try {
      this._bundleJson = JSON.stringify(decoded, null, 2);
    } catch (e) {
      this._bundleJson = `Failed to stringify: ${String(e)}`;
    }
  }

  clearSubscription() {
    this._subscription?.unsubscribe();
    this._subscription = null;
    this._isSubscribed = false;
    this._headline = '×';
    this._bundleJson = '';
    this._promiseResult = '';
  }

  render() {
    return html`
      <div class="app">
        <h1>IDP Bundle Rig${this._lane ? ` (${this._lane})` : ''}</h1>
        <h2>${this._headline}</h2>
        <h3>Promise: ${this._promiseResult}</h3>
        ${this._bundleJson
          ? html`<pre>${this._bundleJson}</pre>`
          : html`<p><em>Not subscribed</em></p>`}
        <ul>
          <li>
            <label>Lane</label>
            <input
              type="text"
              @change=${this.onLaneChanged}
              .value=${this._lane || ''}
              placeholder="lane or empty for 'base'"
            />
            <button @click=${() => this.onSetLane('AAAA')}>AAAA</button>
            <button @click=${() => this.onSetLane('BBBB')}>BBBB</button>
            <button @click=${() => this.onSetLane(undefined)}>Clear</button>
          </li>
          <li>
            <label>
              <input
                type="checkbox"
                @change=${this.onSubscriptionRequestChanged}
                .checked=${this._isSubscribed}
              />
              Subscribe to IDP bundle stream
            </label>
          </li>
          <li>
            <button @click=${this.onGetBundle}>
              Get IDP Bundle (one-shot)
            </button>
          </li>
        </ul>
      </div>
    `;
  }

  static styles = rigSharedStyles;
}

declare global {
  interface HTMLElementTagNameMap {
    'heimdall-rig-idp-bundle': HeimdallRigIdpBundle;
  }
}
